"use client";

import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Key, MessageCircle } from "lucide-react";
export default function TheArchitect() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section id="the-architect" className="relative bg-background py-20 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        {}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-[var(--font-cinzel)] tracking-[4px] leading-[1.35] text-accent bg-dark-background px-8 py-6 rounded-lg inline-block !font-bold mb-6 !text-[40px]">Your Family Wisdom Legacy Architect</h2>

          <div className="font-['Lato'] text-[24px] tracking-[6px] leading-[1.35] text-[#d4af37] italic mt-[10px] text-center w-full !font-sans !font-semibold">The Heart, Mind and Hands Crafting Your Family Wisdom Legacy</div>
        </div>

        {}
        <div ref={textRef} className={`grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-12 lg:gap-16 items-start mt-[40px] transition-all duration-[350ms] ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
          {}
          <div className="space-y-5 px-[30px] md:px-0 w-[90%] md:w-full">
            <p className="font-[var(--font-libre-baskerville)] text-lg leading-[1.6] text-foreground-light"> Architecting a permanent record of your life's journey requires a disciplined integration of digital infrastructure and narrative continuity. As your Family Wisdom Legacy Architect, I bring <b>over 30 years of professional expertise in marketing and digital technology</b> to ensure your legacy is engineered with technical excellence.</p>

            <p className="font-[var(--font-libre-baskerville)] text-lg leading-[1.6] text-foreground-light">
              My <b>15-year tenure with Worldwide Marriage Encounter</b> has uniquely refined my approach, instilling a deep understanding of the structural importance of family communication and the enduring impact of the spoken word.
            </p>

            <p className="font-[var(--font-libre-baskerville)] text-lg leading-[1.6] text-foreground-light">Approaching my <b>60th birthday</b> prompted a period of deep reflection. I found myself asking: <b><i className="!italic">"What is the most significant impact I can make for families and the generations that follow?</i></b>"</p>

            <p className="font-[var(--font-libre-baskerville)] text-lg leading-[1.6] text-foreground-light">The answer was clear: my purpose is to build a bridge between generations. <b>Family Legacy Voices represents the pinnacle of my 30-year career—the point where my technical expertise meets my deepest personal values.</b> I have dedicated this chapter of my life to working intimately with those who value the preservation of truth and the enduring power of a family's collective wisdom.</p>

            <div className="bg-accent text-foreground-light p-6 md:p-8 rounded-lg shadow-medium my-6">
              <p className="font-[var(--font-libre-baskerville)] text-lg leading-[1.6] italic">"I believe that a family's greatest wealth is the wisdom of its elders. My professional mission is to ensure that your life's journey isn't just recorded, but precisely architected to be heard and felt for generations to come."</p>
            </div>

            <p className="font-[var(--font-libre-baskerville)] text-lg leading-[1.6] text-foreground-light">This mission-driven approach allows me to structure my fees to provide <b>exceptional value</b> to the families I serve. By maintaining a dedicated personal practice, I am able to offer a <b>bespoke, high-touch process </b> and a level of individual oversight that larger commercial firms rarely provide. Furthermore, a portion of the proceeds from every project is donated to organisations dedicated to <b>supporting and strengthening families</b>.</p>

            <p className="font-[var(--font-libre-baskerville)] text-lg leading-[1.6] text-foreground-light">
              My focus is not on scale, but on the depth and integrity of the work. To maintain this standard, I honor <b>'The Six-Six Commitment</b>.' This ensures I am personally present to architect a family wisdom legacy designed to endure for generations.
            </p>
          </div>

          {}
          <div className="relative">
            <div className="overflow-hidden rounded-lg shadow-medium border border-accent">
              <img src="https://familylegacyvoices.com/wp-content/uploads/2026/01/ancel_photo_sitting_bluewaters_2.png" alt="Your Legacy Architect - Professional headshot" className="w-full h-auto object-cover transition-transform duration-[350ms] ease-out hover:scale-[1.02]" />
            </div>
          </div>
        </div>

        {}
        <div className="mt-16 bg-secondary/30 rounded-lg p-8 md:p-12 border border-muted">
          <div className="text-center mb-8">
            <h3 className="font-[var(--font-montserrat)] font-light text-[24px] tracking-[6px] leading-[1.35] text-accent-900">
              The Six-Six Commitment
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center md:text-left">
              <p className="font-[var(--font-libre-baskerville)] text-lg leading-[1.6] text-foreground-light">
                <b className="text-accent-900">The 6-Week Journey:</b> We spend
                six dedicated weeks architecting your archive, moving from
                initial dialogue to the final delivery of your Heritage Compendium.
              </p>
            </div>

            <div className="text-center md:text-left">
              <p className="font-[var(--font-libre-baskerville)] text-lg leading-[1.6] text-foreground-light">
                <b className="text-accent-900">The 6-Family Limit:</b> I work
                with a maximum of six families at a time. This ensures that I
                personally present for every interview and every synthesis,
                giving your story the focused attention it requires.
              </p>
            </div>
          </div>
        </div>

        {}
        <div ref={ctaRef} className={`mt-12 flex flex-col items-center px-4 md:px-0 transition-all duration-700 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Button asChild className="cta-light w-full md:w-auto max-w-lg whitespace-normal break-words px-6 py-4 min-h-[60px] text-sm sm:text-base font-semibold rounded-lg shadow-medium hover:shadow-strong hover:scale-[1.02] active:scale-100 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] flex items-center justify-center text-center">
            <Link to="https://calendly.com/ancelfernandes/30-min-strategy-call" newTab>
              <span className="block text-center">BOOK COMPLIMENTARY CONSULTATION CALL</span>
            </Link>
          </Button>
          <p className="font-[var(--font-libre-baskerville)] text-[14px] italic text-foreground mt-[10px] mb-[15px] flex items-center justify-center gap-2 flex-wrap">
            <Key className="size-4" />
            <span>Includes a private viewing of <b className="italic">The Heritage Experience</b> and <b className="italic">The Heritage Compendium</b>.</span>
          </p>
          
          <div className="flex justify-center w-full" style={{
          paddingTop: '15px'
        }}>
            <Button asChild variant="ghost" className="w-full md:w-auto max-w-lg whitespace-normal break-words px-6 py-3 text-sm font-semibold rounded-lg border-[1.5px] border-[#D4AF37] text-[#D4AF37] bg-transparent hover:bg-[#D4AF37] hover:text-white transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] flex items-center justify-center gap-2">
              <Link to="https://wa.me/971504585183?text=I%20am%20interested%20in%20learning%20more%20about%20how%20I%20could%20preserve%20my%20family%20wisdom%20legacy." newTab>
                <MessageCircle className="size-5" />
                <span>ENQUIRE VIA WHATSAPP</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}